import { ThunkAction } from 'redux-thunk';
import moment from 'moment';
import { getFormInitialValuesFromEntityResponse, getDynamicReqsPayload, filterTimelineEvents, getFormattedDate } from '../../components/custom/forms/form-helper-methods';
import { AppState } from '../app-state';
import { EntityDraftViewModel } from '../../interfaces/EntityDraftViewModel';
import {
  getLookups, evaluatePolicyEngine,
  getEntityDraftData, updateEntityDraft,
  getDocumentUrl,
  getAssociations,
  getLookupLinks,
  getRequirementsInScope,
  getEntityDocuments
} from '../../services/fenergo-service';
import {
  setDocumentRequirements, setDocumentRequirementsLoadInProgress, setDocuments, setDocumentsLoadInProgress,
  setEntityDraft, setEntityDraftLoadInProgress, setLookups, setLookupsLoadInProgress, setDataGroups, setDataGroupsLoadInProgress,
  setAssociationsLoadInProgress, setAssociations, setEntitiesLoadInProgress,
  setDocumentDownloadUrlLoadInProgress, setDocumentDownloadUrl, setVerifyingEntityInformation, setLookupLinks, setLookupLinksLoadInProgress, setCodatIntegrationLoadInProgress, setCodatIntegration, setJurisdictions
} from '../entity/entity-action';
import { setCodatState, setSnackBar } from '../ui/ui-action'
import { downloadWorkbook, editPersonalDetails, getAccountMappings, getCompanyDetails, getDataGroups, getUserInfo, getWorkbookRecords, verifyEntity, getTimelineRecords, getPdsEligibilityStatus } from '../../services/bff-service';
import FENERGO_CONSTANTS from '../../utils/fenergo-constants';
import { getAllIntegrations, getCompany, getCompanyId, LINKED_STATUS } from '../../services/codat-service';
import { setCompanyDetails, setWorkbookRecords, setTimelineRecords, setWorkbookUrl, setAccountMappings,  setAccountBucketInfo, setUnBucketedAccounts, setPdsEligibleStatus, updateIsPdsDetailsUnavailable } from '../company/company-action';
import { saveUserInfoAction, setPersonalDetails } from '../user/user-action';
import {
  getInitialBucketIdAndLabel,
  getUnBucketedAccountsMap,
  isNotEmpty,
  windowRedirectTo
} from '../../utils/common-utils';

type AppThunk = ThunkAction<void, AppState, null, any>;

export function fetchLookups(): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(setLookupsLoadInProgress(true));
    try {
      const lookups = await getLookups();
      const activeLookups = lookups.map(
        lookup => lookup.versions.find(version => version.isActive && version.status === FENERGO_CONSTANTS.PUBLISHED)
      ).filter(l => l)
      dispatch(setLookups(activeLookups));
    } catch (error) {
      dispatch(setSnackBar({ show: true, message: 'Error while calling API', description: error.message, messageType: 'error' }));
    }
    dispatch(setLookupsLoadInProgress(false));
  };
}

export function getCodatIntegration(): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(setCodatIntegrationLoadInProgress(true));
    try {
      const integrations = await getAllIntegrations();
      dispatch(setCodatIntegration(integrations));
    } catch (error) {
      dispatch(setSnackBar({ show: true, message: 'Error while retrieving Accounting integrations', description: error.message, messageType: 'error' }));
    }
    dispatch(setCodatIntegrationLoadInProgress(false));
  };
}
export function fetchCodatConnectInformationByCompanyId(companyId: string): AppThunk {
  return async (dispatch): Promise<void> => {
    try {
      const company = await getCompany(companyId)
      const dataConnection = company?.dataConnections.find(conn => conn.status === LINKED_STATUS);
      if (dataConnection) {
        dispatch(setCodatState({
          companyName: company.name,
          platformName: dataConnection.platformName,
          statusCode: '200',
          errorMessage: null,
          companyId: companyId,
          integration: 'codat',
          step: null
        }));
      }
    } catch (error) {
      console.info('No connected accounting company found. Assuming company is not yet created')
      return;
    }
  }
}

export function fetchCodatConnectionInformation(adfId?:string): AppThunk {
  return async (dispatch, getState): Promise<void> => {
    let companyId;
    try {
      const dlaXavierCompanyId = getState()?.entity?.selectedEntity?.xavier_company_id
      companyId = await getCompanyId(adfId, dlaXavierCompanyId)
    } catch (error) {
      console.info('No connected accounting company found companyId. Assuming company is not yet created');
      return;
    }
    let company;
    try {
      if (companyId) {
        company = await getCompany(companyId);
      }
    } catch (error) {
      console.info('No connected accounting company found. Assuming company is not yet created')
      return;
    }
    const dataConnection = company?.dataConnections.find(conn => conn.status === LINKED_STATUS);

    if (dataConnection) {
      dispatch(setCodatState({
        companyName: company.name,
        platformName: dataConnection.platformName,
        statusCode: '200',
        errorMessage: null,
        companyId: companyId,
        integration: 'codat',
        step: null
      }));
    } else if (company) {
      dispatch(setCodatState({
        companyName: company.name,
        companyId: company.id
      }))
    }
  }
}

export function fetchLookupLinks(): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(setLookupLinksLoadInProgress(true));
    try {
      const lookupLinks = await getLookupLinks();
      const activeLookupLinks = lookupLinks.map(
        lookupLink => lookupLink.versions.find(version => version.isActive && version.status === FENERGO_CONSTANTS.PUBLISHED)
      )
      dispatch(setLookupLinks(activeLookupLinks));
    } catch (error) {
      dispatch(setSnackBar({ show: true, message: 'Error while calling API', description: error.message, messageType: 'error' }));
    }
    dispatch(setLookupLinksLoadInProgress(false));
  };
}

export function fetchDatagroups(): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(setDataGroupsLoadInProgress(true));
    try {
      const dataGroups = await getDataGroups();
      const activeDataGroups = dataGroups.map(
        dataGroup => dataGroup.versions.find(version => version.isActive && version.status === FENERGO_CONSTANTS.PUBLISHED)
      ).filter(f => f);
      dispatch(setDataGroups(activeDataGroups));
      dispatch(setDataGroupsLoadInProgress(false));
    } catch (error) {
      dispatch(setSnackBar({ show: true, message: 'Error while calling API', description: error.message, messageType: 'error' }));
      dispatch(setDataGroupsLoadInProgress(false));
    }
  
  };
}

export function fetchDocumentRequirements(entityDraft: EntityDraftViewModel, currentTask: object): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(setDocumentRequirementsLoadInProgress(true));
    const properties = getDynamicReqsPayload(getFormInitialValuesFromEntityResponse(entityDraft));
    const requirements = await getRequirementsInScope(entityDraft["type"], currentTask["policyRequirementType"], currentTask["policyTarget"], currentTask["businessCategory"], entityDraft.jurisdictions);
    const moreReqs = await evaluatePolicyEngine(['Data', 'Document'], currentTask["businessCategory"], properties, entityDraft.jurisdictions, currentTask["policyTarget"]);
    const documentReq = requirements.concat(moreReqs);

    dispatch(setDocumentRequirements(documentReq));
    dispatch(setDocumentRequirementsLoadInProgress(false));
  };
}

export function fetchUploadedDocuments(journeyId: string, entityId: string): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(setDocumentsLoadInProgress(true));
  
    const entityDocuments = await getEntityDocuments(entityId);
    dispatch(setDocuments(entityDocuments));
    dispatch(setDocumentsLoadInProgress(false));
  };
}

export function fetchDocumentUrl(documentId: string): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(setDocumentDownloadUrlLoadInProgress(true));
    const documentDownloadUrl = await getDocumentUrl(documentId);
    dispatch(setDocumentDownloadUrl(documentId, documentDownloadUrl));
    dispatch(setDocumentDownloadUrlLoadInProgress(false));
  };
}

export function fetchEntityDraft(entityId: string, journeyId?: any): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(setEntityDraftLoadInProgress(true));
    try {
      const entityDrafts = await getEntityDraftData(entityId);
      let journeyEntityDraft = null;
      if (journeyId) {
        journeyEntityDraft = entityDrafts.find(x => x.journeyId === journeyId);
      }
      if (!journeyEntityDraft) {
        journeyEntityDraft = entityDrafts[0];
      }

      dispatch(setEntityDraft(journeyEntityDraft));
      dispatch(setJurisdictions(journeyEntityDraft.jurisdictions));
    } catch (error) {
      dispatch(setSnackBar({ show: true, message: 'Error while calling API', description: error.message, messageType: 'error' }));
    }
    dispatch(setEntityDraftLoadInProgress(false));
  };
}

export function fetchAssociations(entityId: string, journeyId: string): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(setAssociationsLoadInProgress(true));
    try {
      const associations = await getAssociations(entityId, journeyId);
      dispatch(setAssociations(associations));
    } catch (error) {
      dispatch(setSnackBar({ show: true, message: 'Error while calling API', description: error.message, messageType: 'error' }));
    }
    dispatch(setAssociationsLoadInProgress(false));
  };
}

export function verifyEntityInformation(entityId: string, draftId: string, formData: any, journeyId: string, isUKJurisdiction: boolean) {
  return async (dispatch): Promise<void> => {
    dispatch(setVerifyingEntityInformation(!isUKJurisdiction));

    try {
      await updateEntityDraft(entityId, draftId, formData);
      await verifyEntity(entityId);
      dispatch(fetchUploadedDocuments(journeyId, entityId));

      const entityDraft = await getEntityDraftData(entityId);
      dispatch(setEntityDraft(entityDraft[0]));
      dispatch(setVerifyingEntityInformation(false));
    }
    catch (error) {
      dispatch(setSnackBar({ show: true, message: 'Error while verifying entity details', description: error.message, messageType: 'error' }));
    }
  }
}

export function saveEntityInformation(entityId: string, draftId: string, formData: any, journeyId: string, isUKJurisdiction?: boolean) {
  return async (dispatch): Promise<void> => {
    try {
      await updateEntityDraft(entityId, draftId, formData);
      dispatch(fetchUploadedDocuments(journeyId, entityId));
      const entityDraft = await getEntityDraftData(entityId);
      dispatch(setEntityDraft(entityDraft[0]));
      dispatch(setVerifyingEntityInformation(false));
    }
    catch (error) {
      dispatch(setSnackBar({ show: true, message: 'Error while saving entity details', description: error.message, messageType: 'error' }));
    }
  }
}

export function fetchCompanyDetails(id:string){
  return async (dispatch):Promise<void>=>{
    try{
        const response= await getCompanyDetails(id)
        dispatch(setCompanyDetails(response))
    } catch (error) {
      const statusCode = error?.response?.data?.error?.statusCode;
      dispatch(setSnackBar({
        show: true,
        message: 'Error while fetching Company Details',
        description: error.message,
        messageType: 'error'
      }))
      if (statusCode === 403) windowRedirectTo('/svber/data-management/companies/view?tab=allCompanies')
    }
  }
}

export function fetchPdsEligibleStatus(payload, defaultState?: any) {
  return async (dispatch): Promise<void> => {
    try {
      const pdsEligibilityDetails = await getPdsEligibilityStatus(payload);
      dispatch(updateIsPdsDetailsUnavailable(false))
      dispatch(setPdsEligibleStatus(pdsEligibilityDetails));
    } catch (error) {
      if(error?.status === 422 || error?.status >= 500) dispatch(updateIsPdsDetailsUnavailable(true))
      else dispatch(setPdsEligibleStatus(defaultState));
      dispatch(
        setSnackBar({
          show: true,
          message: "Error while fetching Restatement Status",
          description: error.message,
          messageType: "error",
        })
      );
    }
  };
}

export function savePersonalDetails(request: any) {
  return  async (dispatch): Promise<void> => {
    try {

       const response = await editPersonalDetails(request)
       dispatch(setPersonalDetails(response)) 
       dispatch(setSnackBar({
        show: true,
        message: 'Applicant info updated.',
        messageType: "success"
      }))
    } catch(error) {
      dispatch(setSnackBar({show:true, message:'Action failed. Please try again.',description:'',messageType:'error'}))
    }
  }
}

export function fetchUserInfo(): AppThunk {
  return async (dispatch): Promise<void> => {
    try {
      const userInfo = await getUserInfo();
      dispatch(saveUserInfoAction(userInfo));
    } catch (error) {
      dispatch(setSnackBar({ show: true, message: 'Error while retrieving Accounting integrations', description: error.message, messageType: 'error' }));
    }
  };
}

export function fetchWorkbookRecords(id: string, signal: AbortSignal): AppThunk {
  return async (dispatch): Promise<void> => {
    try {
      const workbookRecords = await getWorkbookRecords(id, signal);
      dispatch(setWorkbookRecords(workbookRecords))
    } catch (error) {
      console.log(error?.code )
      if(error?.code !== 'ERR_CANCELED' && error?.code != 500)
        dispatch(setSnackBar({ show: true, message: 'Error while retrieving workbook records', description: error.message, messageType: 'error' }))
    }
  };
}

export function fetchTimelineRecords(id: string,  signal: AbortSignal, month?: string, readOnlyMode?: boolean): AppThunk {
  
  const selectedMM = month?.split(' ')[0]
  const year = parseInt(month?.split(' ')[1])
  return async (dispatch): Promise<void> => {
    const date = getFormattedDate(year, selectedMM)

    try {
      let timelineRecords = await getTimelineRecords(id, signal);
      if (!readOnlyMode) {
        timelineRecords = filterTimelineEvents(timelineRecords, date)
      }
      dispatch(setTimelineRecords(timelineRecords));
    } catch (error) {
      console.log(error?.code )
      if(error?.code !== 'ERR_CANCELED' && error?.code !=500)
        dispatch(setSnackBar({ show: true, message: 'Error while retrieving workbook records', description: error.message, messageType: 'error' }));
    }
  };
}

export function downloadWorkbookFile(bucket: string, key: string): AppThunk {
  return async (dispatch): Promise<void> => {
    try {
      const workbookUrl = await downloadWorkbook(bucket, key);
      dispatch(setWorkbookUrl(workbookUrl, key));
    } catch (error) {
      dispatch(setSnackBar({ show: true, message: 'Error while downloading workbook', description: error.message, messageType: 'error' }));
    }
  };
}

export function fetchAccountMappings(id:string){
  return async (dispatch):Promise<void>=>{
    try{
        const response= await getAccountMappings(id);
        dispatch(setAccountMappings(response.results));
        
        dispatch(setAccountBucketInfo(getInitialBucketIdAndLabel(response.results)));

        dispatch(setUnBucketedAccounts(getUnBucketedAccountsMap(response.results)));

    }
    catch(error){
      dispatch(setSnackBar({show:true, message:'Error while fetching Account Mappings Details',description:error.message,messageType:'error'}))
    }
  }
}